import { Button, Flex, Link, ModalV2, QueryLoader, Text } from "@jsq/ds/core";
import { useExistingPositionsQuery } from "~/graphql/supergraphHooks";
import { getInvestingProfileName } from "~/pages/Compliance/CaseDetailsDrawer/Profiles/utils";
import { PositionsTable } from "./PositionsTable";
import { footer, intro } from "./styles_vanilla.css";
import { PositionRowData } from "./types";
import { buildViewAllPositionsHref, profileRows } from "./utils";

type CommonProps = {
  onClose: () => void;
};

/** Opens the modal for a single compliance profile — queries that profile's positions. */
type ProfileModeProps = CommonProps & {
  profileId: number;
  profileName?: string;
};

/**
 * Opens the modal for an investment account. The account's positions span multiple owner
 * profiles, so the caller passes the already-loaded rows rather than a single profile id.
 */
type AccountModeProps = CommonProps & {
  accountName: string;
  rows: PositionRowData[];
  viewAllHref: string;
};

export type ExistingPositionsModalProps = ProfileModeProps | AccountModeProps;

const isAccountMode = (props: ExistingPositionsModalProps): props is AccountModeProps =>
  "rows" in props;

export const ExistingPositionsModal = (props: ExistingPositionsModalProps) => {
  const accountProps = isAccountMode(props) ? props : null;
  const profileProps = isAccountMode(props) ? null : props;

  const profileResult = useExistingPositionsQuery({
    variables: { profileId: profileProps?.profileId ?? 0 },
    skip: profileProps === null,
    fetchPolicy: "cache-first",
  });

  const profile = profileResult.data?.complianceProfile;
  const profileName = profile
    ? getInvestingProfileName(profile)
    : (profileProps?.profileName ?? "");

  const subtitle = accountProps ? accountProps.accountName : profileName;
  const rows = accountProps
    ? accountProps.rows
    : profileRows(profileName, profile?.positions ?? []);
  const viewAllHref = accountProps
    ? accountProps.viewAllHref
    : buildViewAllPositionsHref(profile?.positions ?? []);

  const table = <PositionsTable subtitle={subtitle} rows={rows} />;

  return (
    <ModalV2
      data-testid="existing-positions-modal"
      show={true}
      onHide={props.onClose}
      size="large"
      title="Existing positions of compliance profile"
      description={subtitle}
    >
      <ModalV2.Body>
        <Text className={intro} variant="secondary-md">
          Review the existing positions the selected compliance profile is already associated
          with.
        </Text>
        {accountProps ? table : <QueryLoader result={profileResult}>{table}</QueryLoader>}
      </ModalV2.Body>
      <ModalV2.Footer>
        <Flex className={footer}>
          {rows.length > 0 && (
            <Link
              href={viewAllHref}
              shouldOpenInNewTab
              icon="external-link"
              iconPosition="right"
              data-testid="view-all-positions-link"
            >
              View all related positions
            </Link>
          )}
          <Button
            data-testid="existing-positions-close"
            variant="neutral"
            onClick={props.onClose}
          >
            Close
          </Button>
        </Flex>
      </ModalV2.Footer>
    </ModalV2>
  );
};
